import {
  Card,
  CardContent,
  Box,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { IPdfRepport } from "../../shared/types/pdfRepports.type";
import palette from "../../theme/palette";
import { formatDate } from "../../shared/utils/DateUtils";
import { t } from "i18next";
import { useAuth } from "../../hooks/useAuth";

function FileCard({
  file,
  onDownload,
  isDownloading,
}: {
  isDownloading: boolean;
  file: IPdfRepport;
  onDownload: (
    url: string,
    filename: string,
    type: IPdfRepport["type"],
  ) => void;
}) {
  const { getUserDateFormat } = useAuth();

  const getFileIconProps = (type: string) => {
    switch (type) {
      case "GALLOP_REVIEW":
        return {
          icon: "mdi:horse-variant-fast",
          backgroundColor: "#1E3A5F",
          color: "#64B5F6",
        };

      case "TRAINING":
        return {
          icon: "mdi:horse-variant",
          backgroundColor: palette.dark.primary.dark,
          color: palette.dark.primary.light,
        };

      case "STABLE_REVIEW":
        return {
          icon: "mdi:barn",
          backgroundColor: "#1B5E20",
          color: "#81C784",
        };

      default:
        return {
          icon: "mdi:file-document-outline",
          backgroundColor: "#37474F",
          color: "#B0BEC5",
        };
    }
  };

  const { icon, backgroundColor, color } = getFileIconProps(file.type);

  return (
    <Card sx={{ minHeight: "100%" }}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Card
          sx={{
            p: 2,
            width: 60,
            minWidth: 60,
            flexShrink: 0,
            height: 60,
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor,
          }}
        >
          <Icon
            style={{ flexShrink: 0 }}
            icon={icon}
            color={color}
            width={28}
            height={28}
          />
        </Card>

        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {formatDate(file.sent_date, getUserDateFormat, "en", true)} |{" "}
            {file.filename}
          </Typography>

          <Tooltip
            arrow
            title={
              <div dangerouslySetInnerHTML={{ __html: file.description }} />
            }
            slotProps={{
              tooltip: {
                sx: {
                  "& ul": {
                    margin: 0,
                    paddingLeft: "16px",
                    listStyleType: "disc",
                  },
                },
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#A0A0A0",
                "& ul": {
                  margin: 0,
                  paddingLeft: "16px",
                  listStyleType: "disc",
                },
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: file.description }} />
            </Typography>
          </Tooltip>
        </Box>

        <IconButton
          disabled={isDownloading}
          onClick={() => onDownload(file.url, file.filename, file.type)}
          sx={{ alignSelf: "center", flexShrink: 0, ml: "auto" }}
        >
          <Tooltip title={t("button:download")}>
            <Icon icon="heroicons-outline:download" width={20} height={20} />
          </Tooltip>
        </IconButton>
      </CardContent>
    </Card>
  );
}

export default FileCard;
